import { getMetricStorageKey, resolveMetricForView } from './metricHelpers';

const STORAGE_KEY = 'siq:ui:v2:anon:default:dashboardState';
const LEGACY_SELECTED_FIELDS_KEY = 'selectedfields';
const LEGACY_DASHBOARD_MULTI_SELECT_KEY = 'dashboardMultiSelect';

const DEFAULT_STATE = {
  version: 2,
  selectedDashboardId: null,
  globalContext: {
    activeMetric: null,
    activeCategories: [],
    activeTickers: [],
  },
  dashboards: {
    industryMetrics: {
      view: null,
      metricDashboard: {
        metrics: null,
        allowMulti: null,
      },
    },
    securityAnalysis: {
      byTicker: {},
    },
    marketCoverage: {
      view: null,
      categories: null,
      selectedTickers: [],
    },
  },
};

const cloneDefaultState = () => JSON.parse(JSON.stringify(DEFAULT_STATE));

const safeParse = (raw, fallback = null) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const mergeState = (stored) => ({
  ...cloneDefaultState(),
  ...(stored || {}),
  globalContext: {
    ...DEFAULT_STATE.globalContext,
    ...(stored?.globalContext || {}),
  },
  dashboards: {
    ...cloneDefaultState().dashboards,
    ...(stored?.dashboards || {}),
    industryMetrics: {
      ...DEFAULT_STATE.dashboards.industryMetrics,
      ...(stored?.dashboards?.industryMetrics || {}),
      metricDashboard: {
        ...DEFAULT_STATE.dashboards.industryMetrics.metricDashboard,
        ...(stored?.dashboards?.industryMetrics?.metricDashboard || {}),
      },
    },
    securityAnalysis: {
      ...DEFAULT_STATE.dashboards.securityAnalysis,
      ...(stored?.dashboards?.securityAnalysis || {}),
    },
    marketCoverage: {
      ...DEFAULT_STATE.dashboards.marketCoverage,
      ...(stored?.dashboards?.marketCoverage || {}),
    },
  },
});

const applyLegacyMigration = (state) => {
  const migrated = mergeState(state);

  const legacyFields = safeParse(localStorage.getItem(LEGACY_SELECTED_FIELDS_KEY), null);
  if (Array.isArray(legacyFields) && legacyFields.length > 0) {
    if (!migrated.globalContext.activeMetric) {
      migrated.globalContext.activeMetric = getMetricStorageKey(legacyFields[0]);
    }
    if (!migrated.dashboards.industryMetrics.metricDashboard.metrics) {
      migrated.dashboards.industryMetrics.metricDashboard.metrics = legacyFields.map(getMetricStorageKey);
    }
  }

  if (migrated.dashboards.industryMetrics.metricDashboard.allowMulti === null) {
    const legacyAllowMulti = localStorage.getItem(LEGACY_DASHBOARD_MULTI_SELECT_KEY);
    if (legacyAllowMulti !== null) {
      migrated.dashboards.industryMetrics.metricDashboard.allowMulti = legacyAllowMulti === 'true';
    }
  }

  return migrated;
};

export const readDashboardState = () => {
  try {
    return applyLegacyMigration(safeParse(localStorage.getItem(STORAGE_KEY), null));
  } catch {
    return cloneDefaultState();
  }
};

export const writeDashboardState = (updater) => {
  try {
    const current = readDashboardState();
    const next = typeof updater === 'function' ? updater(current) : updater;
    const merged = mergeState(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new CustomEvent('dashboardStateChange', { detail: merged }));
    return merged;
  } catch {
    return readDashboardState();
  }
};

export const setSelectedDashboardId = (dashboardId) => {
  writeDashboardState((state) => ({
    ...state,
    selectedDashboardId: dashboardId,
  }));
};

export const getGlobalActiveMetric = () => {
  return readDashboardState().globalContext.activeMetric;
};

export const setGlobalActiveMetric = (fieldName) => {
  if (!fieldName) return;
  const activeMetric = getMetricStorageKey(fieldName);
  writeDashboardState((state) => ({
    ...state,
    globalContext: {
      ...state.globalContext,
      activeMetric,
    },
  }));
};

export const getGlobalActiveCategories = () => {
  const categories = readDashboardState().globalContext.activeCategories;
  return Array.isArray(categories) ? categories : [];
};

export const setGlobalActiveCategories = (categories) => {
  if (!Array.isArray(categories)) return;
  writeDashboardState((state) => ({
    ...state,
    globalContext: {
      ...state.globalContext,
      activeCategories: categories,
    },
  }));
};

export const getGlobalActiveTickers = () => {
  const tickers = readDashboardState().globalContext.activeTickers;
  return Array.isArray(tickers) ? tickers : [];
};

export const setGlobalActiveTickers = (tickers) => {
  if (!Array.isArray(tickers)) return;
  writeDashboardState((state) => ({
    ...state,
    globalContext: {
      ...state.globalContext,
      activeTickers: tickers,
    },
  }));
};

export const getDashboardMetricSelection = (
  dashboardId,
  defaultMetrics,
  { targetView = 'dashboard', validFields } = {}
) => {
  const state = readDashboardState();
  const dashboardMetrics = state.dashboards?.[dashboardId]?.metricDashboard?.metrics;
  const scopedMetrics = Array.isArray(dashboardMetrics) && dashboardMetrics.length > 0
    ? dashboardMetrics
    : [];

  const resolveMetrics = (metrics) => metrics
    .map(metric => resolveMetricForView(metric, targetView, { validFields, fallback: null }))
    .filter(Boolean);

  const scopedResolved = resolveMetrics(scopedMetrics);
  if (scopedResolved.length > 0) return scopedResolved;

  const globalResolved = state.globalContext.activeMetric
    ? resolveMetrics([state.globalContext.activeMetric])
    : [];

  return globalResolved.length > 0 ? globalResolved : defaultMetrics;
};

export const setDashboardMetricSelection = (
  dashboardId,
  fields,
  { updateGlobal = true } = {}
) => {
  const metrics = (Array.isArray(fields) ? fields : [fields]).filter(Boolean).map(getMetricStorageKey);
  if (metrics.length === 0) return;

  writeDashboardState((state) => ({
    ...state,
    selectedDashboardId: dashboardId,
    globalContext: {
      ...state.globalContext,
      activeMetric: updateGlobal ? metrics[0] : state.globalContext.activeMetric,
    },
    dashboards: {
      ...state.dashboards,
      [dashboardId]: {
        ...(state.dashboards?.[dashboardId] || {}),
        metricDashboard: {
          ...(state.dashboards?.[dashboardId]?.metricDashboard || {}),
          metrics,
        },
      },
    },
  }));
};

export const getDashboardBooleanSetting = (dashboardId, settingKey, fallback = false) => {
  const value = readDashboardState().dashboards?.[dashboardId]?.metricDashboard?.[settingKey];
  return typeof value === 'boolean' ? value : fallback;
};

export const setDashboardBooleanSetting = (dashboardId, settingKey, value) => {
  writeDashboardState((state) => ({
    ...state,
    dashboards: {
      ...state.dashboards,
      [dashboardId]: {
        ...(state.dashboards?.[dashboardId] || {}),
        metricDashboard: {
          ...(state.dashboards?.[dashboardId]?.metricDashboard || {}),
          [settingKey]: Boolean(value),
        },
      },
    },
  }));
};

export const getResolvedGlobalMetricForView = (targetView, { validFields, fallback } = {}) => {
  const activeMetric = getGlobalActiveMetric();
  if (!activeMetric) return fallback;
  return resolveMetricForView(activeMetric, targetView, { validFields, fallback });
};
